import { deaccent, norm, withinDistanceOne } from "./text.js";

/**
 * Answer checking, per docs/ARCHITECTURE.md §4.
 *
 * "almost" means the child knew the word but tripped over an accent or a
 * single key. It counts as correct for morale and stats, but it does not
 * promote the card to the next Leitner box — see leitner.ts.
 */
export type Verdict = "correct" | "almost" | "wrong";
export type AlmostReason = "accent" | "typo";

export interface AnswerResult {
  verdict: Verdict;
  /** The spelling we show the child. Always the first accepted form. */
  canonical: string;
  /** Only set when verdict is "almost". */
  reason?: AlmostReason;
}

/** Articles that may be omitted, per language. Order matters: longest first. */
const OPTIONAL_ARTICLES = [
  // French
  "les",
  "une",
  "des",
  "der",
  "die",
  "das",
  "den",
  "dem",
  "ein",
  "eine",
  "le",
  "la",
  "un",
  "du",
  "de la",
  // English
  "the",
  "an",
  "a",
];

/** Elided French article glued to the noun: l'école, d'accord. */
const ELIDED_ARTICLE = /^(?:l|d|qu|j|n|s|c|m|t)'/;

const MAX_PAREN_GROUPS = 3;
const TYPO_MIN_LENGTH = 5;

/**
 * Expand one accepted form into every spelling we are willing to accept:
 * each parenthesised group is optional, and a leading article is optional.
 */
function expand(form: string): Set<string> {
  const base = norm(form);
  const withParens = expandParentheses(base);
  const out = new Set<string>();
  for (const variant of withParens) {
    out.add(variant);
    const stripped = stripArticle(variant);
    if (stripped !== variant) out.add(stripped);
  }
  return out;
}

/** "l'école (primaire)" -> ["l'école primaire", "l'école"] */
function expandParentheses(text: string): string[] {
  if (!text.includes("(")) return [text];

  const groups: Array<{ start: number; end: number; inner: string }> = [];
  let depth = 0;
  let start = -1;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "(") {
      if (depth === 0) start = i;
      depth++;
    } else if (text[i] === ")") {
      depth--;
      if (depth === 0 && start >= 0) {
        groups.push({ start, end: i + 1, inner: text.slice(start + 1, i) });
        start = -1;
      }
      if (depth < 0) return [text]; // unbalanced, treat literally
    }
  }
  if (depth !== 0 || groups.length === 0) return [text];
  if (groups.length > MAX_PAREN_GROUPS) return [text.replace(/[()]/g, "")];

  const results: string[] = [];
  const total = 1 << groups.length;
  for (let mask = 0; mask < total; mask++) {
    let built = "";
    let cursor = 0;
    groups.forEach((group, index) => {
      built += text.slice(cursor, group.start);
      if (mask & (1 << index)) built += group.inner;
      cursor = group.end;
    });
    built += text.slice(cursor);
    const cleaned = norm(built);
    if (cleaned.length > 0) results.push(cleaned);
  }
  return results;
}

function stripArticle(text: string): string {
  const elided = text.match(ELIDED_ARTICLE);
  if (elided) return text.slice(elided[0].length).trim();
  for (const article of OPTIONAL_ARTICLES) {
    if (text.startsWith(article + " ")) return text.slice(article.length + 1).trim();
  }
  return text;
}

/** All accepted forms of a solution string, split on "|". */
export function acceptedForms(solution: string): string[] {
  return solution
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * The inverse of acceptedForms: one stored string from separate boxes. The
 * editor shows one box per accepted answer so that no child has to type a bar
 * on an iPad keyboard (issue #26); this is where the bar is put back.
 */
export function joinForms(forms: readonly string[]): string {
  return forms
    .map((form) => form.trim())
    .filter((form) => form.length > 0)
    .join("|");
}

/** The spelling shown to the child when they get it wrong. */
export function canonicalForm(solution: string): string {
  return acceptedForms(solution)[0] ?? solution.trim();
}

export function checkAnswer(input: string, solution: string): AnswerResult {
  const canonical = canonicalForm(solution);
  const forms = acceptedForms(solution);
  if (forms.length === 0) return { verdict: "wrong", canonical };

  const candidates = new Set<string>();
  for (const form of forms) for (const variant of expand(form)) candidates.add(variant);

  const givenVariants = expand(input);
  if (givenVariants.size === 0) return { verdict: "wrong", canonical };

  // 4. Exact match after normalisation.
  for (const given of givenVariants) {
    if (candidates.has(given)) return { verdict: "correct", canonical };
  }

  // 5. Match once diacritics are removed on both sides.
  const foldedCandidates = new Set<string>();
  for (const candidate of candidates) foldedCandidates.add(deaccent(candidate));
  for (const given of givenVariants) {
    if (foldedCandidates.has(deaccent(given))) {
      return { verdict: "almost", canonical, reason: "accent" };
    }
  }

  // 6. One typo, but only on solutions long enough that a single character
  //    is not most of the word. "an" vs "in" must stay wrong.
  for (const candidate of candidates) {
    if (candidate.length < TYPO_MIN_LENGTH) continue;
    for (const given of givenVariants) {
      if (withinDistanceOne(deaccent(candidate), deaccent(given))) {
        return { verdict: "almost", canonical, reason: "typo" };
      }
    }
  }

  return { verdict: "wrong", canonical };
}

/**
 * A tapped tile, not a typed answer: right only if the tile is an accepted
 * form, otherwise wrong. The tolerance in checkAnswer exists for what a child
 * types; on a tile there is no typo, and «Fast» for a neighbouring distractor
 * would pay more than the right tile after «Kacheln zeigen» (issue #65). The
 * matching board compares exactly for the same reason.
 */
export function checkTile(tile: string, solution: string): AnswerResult {
  const checked = checkAnswer(tile, solution);
  return checked.verdict === "correct" ? checked : { verdict: "wrong", canonical: checked.canonical };
}
